/**
 * ConfirmDeleteDialog.jsx
 * --------------------------------------------------------------
 * Generic confirmation dialog for deleting a *single* column.
 * Props
 *  ─ open          : boolean        – show / hide
 *  ─ columnLabel   : string         – label shown in the body text
 *  ─ onConfirm()   : Function       – invoked on Delete
 *  ─ onCancel()    : Function       – invoked on Cancel / backdrop / Esc
 * --------------------------------------------------------------
 */

import React, { forwardRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Slide
} from '@mui/material';

// ------- slide-up transition (keeps dialog from popping) -------
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ConfirmDeleteDialog({
  open = false,
  columnLabel = '',
  onConfirm = () => {},
  onCancel  = () => {}
}) {
  /* helpers */
  const handleClose = (_e, reason) => {
    // MUI passes 'backdropClick' or 'escapeKeyDown'
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-labelledby="confirm-delete-title"
      aria-describedby="confirm-delete-description"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="confirm-delete-title" sx={{ pr: 4 }}>
        Delete column?
      </DialogTitle>

      <DialogContent dividers>
        <Typography id="confirm-delete-description" variant="body2">
          You’re about to permanently delete
          <strong>{` “${columnLabel || 'Unnamed'}” `}</strong>
          from the Display&nbsp;Table.
          <br />
          This can’t be undone.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onCancel}
          autoFocus   // focus goes here on open
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          color="error"
          onClick={onConfirm}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}
